"use client"
import { graphql, useQuery } from "@opensea/graphql"
import { useState } from "react"
import { useTranslations } from "use-intl"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import { useAddress } from "../../../polychain/hooks/useAddress"
import { FlowActions } from "../../../stores/flowStore"
import type { TransferFlowState } from "../../flow-state"
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange"
import { TransferFlowDone } from "./components/TransferFlowDone"
import { TransferFlowDone_itemFragment } from "./components/TransferFlowDone/TransferFlowDone.fragment"
import {
  TransferFlowForm,
  TransferFlowFormSkeleton,
} from "./components/TransferFlowForm"
import { TransferFlowForm_itemFragment } from "./components/TransferFlowForm/TransferFlowForm.fragment"
import { TransferFlowReview } from "./components/TransferFlowReview"
import { TransferFlowReview_itemFragment } from "./components/TransferFlowReview/TransferFlowReview.fragment"
import type { TransferFlowFormValues } from "./types"

const TransferFlowQuery = graphql(
  `
    query TransferFlowQuery(
      $identifiers: [ItemIdentifierInput!]!
      $address: Address
    ) {
      itemsByIdentifiers(identifiers: $identifiers) {
        ...TransferFlowForm_item
        ...TransferFlowDone_item
        ...TransferFlowReview_item
      }
    }
  `,
  [
    TransferFlowForm_itemFragment,
    TransferFlowDone_itemFragment,
    TransferFlowReview_itemFragment,
  ],
)

type Props = Pick<TransferFlowState, "items">

export function TransferFlow({ items: itemsInput }: Props) {
  const t = useTranslations("wallet.TransferFlow")
  const [formValues, setFormValues] = useState<TransferFlowFormValues>()
  const address = useAddress()
  const [{ data, fetching }] = useQuery({
    query: TransferFlowQuery,
    variables: {
      identifiers: itemsInput.map(item => ({
        tokenId: item.tokenId,
        contractAddress: item.contractAddress,
        chain: item.chain.identifier,
      })),
      address,
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  useEndFlowOnAccountChange()

  const [view, setView] = useState<"create" | "review" | "success" | "error">(
    "create",
  )

  const items = data?.itemsByIdentifiers.flatMap(item => (item ? [item] : []))

  const handleFormSubmit = (values: TransferFlowFormValues) => {
    setFormValues(values)
    setView("review")
  }

  if (fetching) {
    return <TransferFlowFormSkeleton count={itemsInput.length} />
  }

  if (!items) {
    throw new Error(t("errorMessage"))
  }

  return (
    <>
      {view === "create" && (
        <TransferFlowForm
          defaultValues={formValues}
          items={items}
          onSubmit={handleFormSubmit}
        />
      )}
      {view === "review" && formValues && (
        <TransferFlowReview
          formValues={formValues}
          items={items}
          onBack={() => setView("create")}
          onError={() => setView("error")}
          onSuccess={() => {
            setView("success")
            FlowActions.onSuccess()
          }}
        />
      )}
      {view === "success" && (
        <TransferFlowDone items={items} title={t("successTitle")} withLink />
      )}
      {view === "error" && (
        <TransferFlowDone
          body={t("errorBody")}
          items={items}
          title={t("errorTitle")}
          withError
        />
      )}
    </>
  )
}
